import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Invitations, from the admin's side.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Dave's invitation lapsed and could not be replaced: a partial unique index
 * treats an expired invite as still open, so the dead row held his only slot
 * and the replacement failed with a constraint name. There was also no invite
 * screen anywhere in the app, so every person joining the company needed
 * somebody who could open the database console.
 *
 * ── Everything goes through issue_invite ───────────────────────────────────
 *
 * RLS would let an admin write these rows directly from the browser, and this
 * module deliberately does not. Issuing is revoke-then-insert, and as two
 * calls a failure between them leaves the person with NO invitation — worse
 * than the expired one they started with. The function does both in one
 * transaction.
 */

export type AccountType = 'tap' | 'supplier';
export type InternalRole = 'tap_admin' | 'tap_member' | 'tap_viewer';

export interface OpenInvite {
  id: string;
  email: string;
  intended_account_type: AccountType;
  intended_internal_role: InternalRole | null;
  supplier_organization_id: string | null;
  supplier_organization_name: string | null;
  invited_by_name: string | null;
  created_at: string;
  expires_at: string;
  /** Computed by the database, not here — see below. */
  is_expired: boolean;
}

export const inviteKeys = {
  all: ['invites'] as const,
  open: () => [...inviteKeys.all, 'open'] as const,
};

async function fetchOpenInvites(client: SupabaseClient): Promise<OpenInvite[]> {
  const { data, error } = await client.rpc('open_invites');
  if (error) {
    throw new Error(error.message);
  }
  // is_expired arrives from the server on purpose. Deriving it here would
  // compare against the browser's clock, and a laptop set to last week would
  // show a dead invitation as live — a support call nobody will diagnose.
  return data ?? [];
}

export function useOpenInvites() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: inviteKeys.open(),
    queryFn: () => fetchOpenInvites(client),
    enabled: access.status === 'approved',
    retry: false,
  });
}

export interface NewInvite {
  email: string;
  accountType: AccountType;
  /** Only read for a TAP invite; the function ignores it for a supplier. */
  internalRole: InternalRole;
  /** Only read for a supplier invite. */
  supplierOrganizationId: string | null;
  days: number;
}

export function useIssueInvite() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewInvite) => {
      const { error } = await client.rpc('issue_invite', {
        p_email: input.email.trim(),
        p_account_type: input.accountType,
        p_internal_role: input.internalRole,
        // The generated types cannot express a nullable argument, so it is
        // narrowed at this one boundary rather than by widening the shape.
        p_supplier_org: input.supplierOrganizationId as string,
        p_days: input.days,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inviteKeys.all });
    },
  });
}

export function useRevokeInvite() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await client.rpc('revoke_invite', { p_invite: inviteId });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inviteKeys.all });
    },
  });
}

/**
 * Whether an address is worth sending to the database.
 *
 * Deliberately loose. Email validation that tries to be clever rejects real
 * addresses — plus-addressing, new TLDs, apostrophes — and the only authority
 * on whether an address works is whether mail arrives. This catches the two
 * mistakes a person actually makes in this box: nothing typed, and a name
 * without a domain.
 */
export function emailProblem(email: string): string | null {
  const clean = email.trim().toLowerCase();
  if (clean === '') {
    return 'Enter an email address.';
  }
  if (clean.includes(' ')) {
    return 'An email address cannot contain a space.';
  }
  if (!clean.includes('@') || clean.startsWith('@') || clean.endsWith('@')) {
    return 'That does not look like an email address.';
  }
  return null;
}

/**
 * True when issuing would replace an invitation already on the list.
 *
 * Deliberately NOT a refusal. Re-inviting is the whole reason this screen
 * exists, and superseding is exactly what issue_invite does — but an admin who
 * is not told will believe they have sent a second invitation beside the
 * first, and then wonder which one the person should use.
 */
export function wouldSupersede(email: string, existing: string[]): boolean {
  const clean = email.trim().toLowerCase();
  return clean !== '' && existing.some((other) => other.toLowerCase() === clean);
}

/**
 * How an invitation reads on the list.
 *
 * "Expired" is not an error state here. The invitation did its job or it did
 * not, and the only useful thing to say is which — with the action beside it,
 * because the answer to an expired invite is always the same: send another.
 */
export function inviteStatus(invite: OpenInvite): { label: string; tone: 'warning' | 'neutral' } {
  return invite.is_expired
    ? { label: 'Expired', tone: 'warning' }
    : { label: `Expires ${invite.expires_at.slice(0, 10)}`, tone: 'neutral' };
}

/** What the person will be once they accept, in words rather than enum names. */
export function roleLabel(invite: OpenInvite): string {
  if (invite.intended_account_type === 'supplier') {
    return invite.supplier_organization_name === null
      ? 'Supplier'
      : `Supplier · ${invite.supplier_organization_name}`;
  }
  switch (invite.intended_internal_role) {
    case 'tap_admin':
      return 'Admin';
    case 'tap_viewer':
      return 'Viewer';
    case 'tap_member':
      return 'Member';
    default:
      return 'TAP';
  }
}

/**
 * The database's errors, in a sentence.
 *
 * `already_has_profile` is the one that matters. It is not a failure — it
 * means the person is already in — and an admin who sees a raw error code
 * will send a second invitation and then a third.
 */
export function inviteErrorMessage(message: string): string {
  if (message.includes('already_has_profile')) {
    return 'That person already has an account. If they cannot sign in, the problem is their account rather than an invitation — check whether it is active.';
  }
  if (message.includes('not_authorized')) {
    return 'Only TAP admins can send invitations.';
  }
  if (message.includes('invalid_email')) {
    return 'That does not look like an email address.';
  }
  if (message.includes('invite_email_blocked')) {
    return 'That address is blocked from the Hub and cannot be invited.';
  }
  if (message.includes('access_invites_account_shape')) {
    return 'A supplier invitation needs an organization, and a TAP invitation must not have one.';
  }
  return message;
}
